import { useState, KeyboardEvent, useEffect, useRef } from "react";
import { Form, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { UseFormRegisterReturn, useFormContext } from "react-hook-form";
import RequiredStar from "../RequiredStar";
import "./style.css";

interface FormGroupMultiInputProps {
  label: string;
  required?: boolean;
  register: UseFormRegisterReturn;
  disabled?: boolean;
}

export default function FormGroupMultiInput({ label, required = false, register, disabled = false }: FormGroupMultiInputProps) {
  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const fieldName = register.name;
  const value: string[] = watch(fieldName) || [];
  const error = errors[fieldName];
  const isMounted = useRef(false);

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (isMounted.current) {
      trigger(fieldName);
    } else {
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), trigger, fieldName]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed) {
        if (!value.includes(trimmed)) {
          setValue(fieldName, [...value, trimmed], { shouldDirty: true });
        }
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      setValue(fieldName, value.slice(0, -1), { shouldDirty: true });
    }
  };

  const removeTag = (tagToRemove: string) => {
    if (!disabled) {
      setValue(
        fieldName,
        value.filter((tag) => tag !== tagToRemove),
        { shouldDirty: true }
      );
    }
  };

  return (
    <Form.Group className="position-relative mb-2">
      <Form.Label>
        {label} {required && <RequiredStar />}
      </Form.Label>
      <div className={`form-control form-control-multi-input ${disabled ? "disabled" : ""} ${error ? "is-invalid" : ""}`}>
        {value.map((tag, index) => (
          <Badge key={index} bg="secondary" className="multi-input-badge">
            {tag}
            {!disabled && (
              <span onClick={() => removeTag(tag)} className="multi-input-remove">
                &times;
              </span>
            )}
          </Badge>
        ))}
        {!disabled && (
          <OverlayTrigger placement="top" overlay={<Tooltip>Press Enter to add item</Tooltip>}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className="multi-input-field"
              placeholder="Type and press Enter..."
            />
          </OverlayTrigger>
        )}
      </div>
      {/* Hidden input to ensure field registration although value is managed via setValue/watch */}
      <input
        type="text"
        className="multi-input-validation-target"
        {...register}
        value={value.length > 0 ? value.join(",") : ""}
        required={required}
      />
      {error && <div className="invalid-feedback d-block">{error.message as string}</div>}
    </Form.Group>
  );
}
